/**
 * Each chart's live state, for the toolbar that shows it.
 *
 * The chart (`ChartGroupView`) is what hears the frames; the toolbar that
 * draws the badge beside ⛶ is rendered by `App`, in another subtree. Module
 * scope with subscribers — `symbol-index.ts`'s shape — rather than a callback
 * threaded up through `SectionGrid`, because the fact changes once a second
 * while live and the grid has no business re-rendering on it.
 */

import type { LiveState } from "./intraday";

/** What the badge shows: the state, and the second's tick that pulses the dot. */
export interface LiveBadge {
  state: LiveState;
  tick: number;
}

const badges = new Map<string, LiveBadge>();
const listeners = new Set<() => void>();

/** Set (or with `null`, clear) one chart's badge. */
export function publishLive(chartGroupId: string, badge: LiveBadge | null): void {
  const prev = badges.get(chartGroupId);
  if (badge === null) {
    if (!prev) return;
    badges.delete(chartGroupId);
  } else {
    if (prev && prev.state === badge.state && prev.tick === badge.tick) return;
    badges.set(chartGroupId, badge);
  }
  for (const l of listeners) l();
}

/** One chart's badge, or `undefined` when it has nothing to say. */
export function liveBadgeOf(chartGroupId: string): LiveBadge | undefined {
  return badges.get(chartGroupId);
}

export function subscribeLive(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
